import { useTranslate } from 'react-polyglot';

import type { ResultsMode } from '@genially/p2p-lib';

import { ResultsModeCard } from '../ResultsModeCard/ResultsModeCard';
import analyticsIllustration from './assets/analytics.svg';
import noResultsIllustration from './assets/no-results.svg';
import rankingIllustration from './assets/ranking.svg';
import { Wrapper } from './ResultsModeSelector.styled';

const RADIO_GROUP_NAME = 'session-config-results-mode';

// Translation keys are written out in full on purpose: building them by interpolating the
// mode would hide them from the translation tooling.
const RESULTS_MODE_OPTIONS: {
  mode: ResultsMode;
  titleKey: string;
  descriptionKey: string;
  illustrationSrc: string;
}[] = [
  {
    mode: 'analytics',
    titleKey: '__new.play.shared.configuration.modal.resultsMode.analytics.title',
    descriptionKey:
      '__new.play.shared.configuration.modal.resultsMode.analytics.description',
    illustrationSrc: analyticsIllustration,
  },
  {
    mode: 'ranking',
    titleKey: '__new.play.shared.configuration.modal.resultsMode.ranking.title',
    descriptionKey:
      '__new.play.shared.configuration.modal.resultsMode.ranking.description',
    illustrationSrc: rankingIllustration,
  },
  {
    mode: 'none',
    titleKey: '__new.play.shared.configuration.modal.resultsMode.none.title',
    descriptionKey: '__new.play.shared.configuration.modal.resultsMode.none.description',
    illustrationSrc: noResultsIllustration,
  },
];

interface ResultsModeSelectorProps {
  mode: ResultsMode;
  onChange: (mode: ResultsMode) => void;
  showAnalyticsOption?: boolean;
  showNoResultsOption?: boolean;
}

export const ResultsModeSelector = ({
  mode,
  onChange,
  showAnalyticsOption = false,
  showNoResultsOption = false,
}: ResultsModeSelectorProps) => {
  const t = useTranslate();

  // Ranking is the baseline mode, the other two are each behind their own feature flag.
  const isModeVisible: Record<ResultsMode, boolean> = {
    analytics: showAnalyticsOption,
    ranking: true,
    none: showNoResultsOption,
  };

  const options = RESULTS_MODE_OPTIONS.filter(option => isModeVisible[option.mode]);

  return (
    <Wrapper $columns={options.length}>
      {options.map(option => (
        <ResultsModeCard
          key={option.mode}
          groupName={RADIO_GROUP_NAME}
          value={option.mode}
          title={t(option.titleKey)}
          description={t(option.descriptionKey)}
          illustrationSrc={option.illustrationSrc}
          isSelected={mode === option.mode}
          onSelect={() => onChange(option.mode)}
        />
      ))}
    </Wrapper>
  );
};
